import { useContext, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  DataTable,
  DataTableSkeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TextInputSkeleton,
  Modal,
} from "carbon-components-react";
import { Add16, Edit20 } from "@carbon/icons-react";
import dayjs from "dayjs";
import useSWR, { useSWRConfig } from "swr";

import { DateTimeFormat } from "../../utils/datetime";
import { SchemaContext, UserContext } from "../../context";
import { SchemaEditor } from "../../components/Editor";
import { useUserData } from "../../hooks";
import { URL, Fetcher as F } from "../../api";

const basicHeaders = [
  {
    key: "record_datetime",
    name: "發現日期",
  },
  {
    key: "recover_datetime",
    name: "恢復日期",
  },
  {
    key: "unusual",
    name: "異常狀況",
  },
  {
    key: "comment",
    name: "備註",
  },
  {
    key: "employee_name",
    name: "操作人員",
  },
  {
    key: "actions",
    name: "編輯/取消",
  },
];
const babyHeaders = [
  {
    key: "puerpera_name",
    name: "媽媽姓名",
  },
  {
    key: "record_datetime",
    name: "發現日期",
  },
  {
    key: "recover_datetime",
    name: "恢復日期",
  },
  {
    key: "unusual",
    name: "異常狀況",
  },
  {
    key: "comment",
    name: "備註",
  },
  {
    key: "employee_name",
    name: "操作人員",
  },
  {
    key: "actions",
    name: "編輯/取消",
  },
];

const headerWithStatus = (showWithBaby) => {
  if (showWithBaby) {
    return babyHeaders;
  }
  return basicHeaders;
};

const createQueryString = ({ bid, queryMap }) => {
  const params = new URLSearchParams();
  params.append("expand", "unusual,baby.puerpera,keyin_employee");
  if (bid) {
    params.append("baby", bid);
  }
  if (queryMap) {
    Object.entries(queryMap).forEach(([key, value]) => {
      params.append(key, value);
    });
  }
  return params.toString();
};

export const BabyUnusualRecordTable = ({
  showWithBaby = false,
  queryMap = {},
}) => {
  const { bid } = useParams();

  const {
    token: { access },
  } = useContext(UserContext);

  const queryString = useMemo(
    () =>
      createQueryString({
        bid,
        queryMap,
      }),
    [bid, queryMap]
  );

  const basicURL = URL.babyUnusual;
  const url = `${basicURL}?${queryString}`;
  const { data } = useSWR(access && [url], F.withToken);

  const isLoading = !data;

  return isLoading ? (
    <DataTableSkeleton headers={headerWithStatus(showWithBaby)} />
  ) : (
    <BabyUnusualTable
      basicURL={basicURL}
      baby={bid}
      items={data}
      showWithBaby={showWithBaby}
    />
  );
};

function BabyUnusualTable({ basicURL, baby, items, showWithBaby }) {
  const { babyUnusual: schema } = useContext(SchemaContext) || {};
  const {
    token: { access },
  } = useContext(UserContext);
  const user = useUserData();
  const { mutate } = useSWRConfig();

  const cleanData = items.map(({ baby, unusual, keyin_employee, ...other }) => {
    return {
      ...other,
      unusual: unusual?.name ?? "",
      employee_name: keyin_employee?.name ?? "-",
      puerpera_name: baby?.puerpera?.name ?? "-",
    };
  });

  const [recordData, setRecordData] = useState();

  const [currentRecordID, setCurrentRecordID] = useState(0);

  const [isEditHistory, setIsEditHistory] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editModalStatus, setEditModalStatus] = useState("inactive");

  const isEditTitle = isEditHistory ? "編輯" : "新增";

  const handleCreate = async () => {
    const { id, ...o } = recordData;
    const payload = { ...o };

    setEditModalStatus("active");
    await F.post(`${basicURL}`, payload);
    mutate([basicURL]);
    setOpenEditModal(false);
  };

  const dataURL = `${basicURL}${currentRecordID}/`;
  const { data: rawData } = useSWR(
    isEditHistory ? access && [dataURL] : null,
    F.withToken
  );

  useEffect(() => {
    if (!rawData || rawData == null) {
      return;
    }
    setRecordData(rawData);
  }, [rawData]);

  const handleEdit = async () => {
    if (currentRecordID === 0 || !rawData) {
      return;
    }

    const { id, ...o } = recordData;
    const payload = { ...o };

    setEditModalStatus("active");
    const r = await F.put(`${basicURL}${currentRecordID}/`, payload);
    mutate([basicURL + r.id], r, false);
    setOpenEditModal(false);
  };

  const handleCreateOrEdit = async () => {
    isEditHistory ? handleEdit() : handleCreate();
  };

  return (
    <>
      <Modal
        open={openEditModal}
        modalHeading={`${isEditTitle}新生兒異常紀錄`}
        primaryButtonText="確認"
        secondaryButtonText="取消"
        loadingStatus={editModalStatus}
        onRequestClose={() => setOpenEditModal(false)}
        onSecondarySubmit={() => setOpenEditModal(false)}
        onRequestSubmit={handleCreateOrEdit}
      >
        {isEditHistory && !recordData ? (
          <TextInputSkeleton />
        ) : (
          <SchemaEditor
            fields={schema.fields}
            excludes={[
              "id",
              "created",
              "modified",
              "keyin_employee",
              "baby",
              "activate_date",
              "deactivate_date",
              "status",
            ]}
            requiredFields={["record_date"]}
            data={recordData}
            onChange={(x) => {
              if (!user) return;
              setRecordData({
                ...x,
                baby: baby,
                keyin_employee: user.employee_id,
              });
            }}
          />
        )}
      </Modal>
      <DataTable rows={cleanData} headers={headerWithStatus(showWithBaby)}>
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
          getToolbarProps,
          getTableContainerProps,
        }) => (
          <TableContainer {...getTableContainerProps()}>
            {!showWithBaby && (
              <TableToolbar
                {...getToolbarProps()}
                aria-label="data table toolbar"
              >
                <TableToolbarContent>
                  <Button
                    renderIcon={Add16}
                    onClick={() => {
                      setIsEditHistory(false);
                      setOpenEditModal(true);
                    }}
                  >
                    新增新生兒異常紀錄
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
            )}
            <Table {...getTableProps()} isSortable>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                      isSortable={header.key !== "actions"}
                    >
                      {header.name}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map((cell) => {
                      const { header = "" } = cell && cell.info;
                      const id = cell.id.split(":")[0];
                      let content;
                      switch (header) {
                        case "record_datetime":
                        case "recover_datetime":
                        case "created":
                        case "modified":
                          if (cell.value !== null) {
                            content = dayjs(cell.value).format(
                              DateTimeFormat.date
                            );
                          } else {
                            content = cell.value;
                          }
                          break;
                        case "puerpera_name":
                        case "unusual":
                        case "comment":
                        case "employee_name":
                          content = cell.value;
                          break;
                        case "actions":
                          content = (
                            <div>
                              <Button
                                kind="ghost"
                                renderIcon={Edit20}
                                iconDescription="編輯"
                                onFocus={() => setCurrentRecordID(id)}
                                onClick={() => {
                                  setIsEditHistory(true);
                                  setOpenEditModal(true);
                                }}
                              />
                            </div>
                          );
                          break;
                        default:
                          content = <span>?</span>;
                      }
                      return <TableCell key={cell.id}>{content}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </>
  );
}
